import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Delay before a held add marks its button busy - a prefetched selection usually settles sooner
const HELD_SUBMIT_BUSY_DELAY = 150;

const QUIET_ADD_TOAST_DURATION = 2500;

const QUIET_ADD_ANNOUNCE_DELAY = 100;

// One quiet add toast shows at a time across the page, so a new add takes down the previous card's toast
/** @type {ProductFormComponent | null} */
let quietAddToastOwner = null;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let quietAddToastTimeout;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quietAddToast - The toast confirming a quiet add on a product card.
 * @property {HTMLElement | undefined} quietAddToastText - The toast's message.
 *
 * @typedef {object} CartAddItem - A line in a JSON `/cart/add` request.
 * @property {number} id - The variant id.
 * @property {number} quantity
 * @property {number} [selling_plan]
 * @property {Record<string, string>} [properties]
 * @property {number} [parent_id] - The variant id of the line this one nests under, added in the same request.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Whether an add made during a selection is waiting for it to settle. */
  #isSubmitHeld = false;

  /** Whether an add request is under way; further adds meanwhile are dropped. */
  #isAdding = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #quietAddAnnounceTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {SubmitEvent} event */
  handleSubmit(event) {
    event.preventDefault();

    const picker = this.#getVariantPicker();
    if (picker?.isPending) {
      this.#submitWhenSettled(picker, event);
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * Holds an add made while a selection is loading, then submits it as an immediate add would, so the variant,
   * selling plan, personalization and add-ons all belong to that selection. Further adds meanwhile are dropped,
   * and a selection whose request failed submits nothing and reports the add as failed.
   * @param {import('@theme/variant-picker').default} picker
   * @param {SubmitEvent} event
   */
  async #submitWhenSettled(picker, event) {
    if (this.#isSubmitHeld) return;
    this.#isSubmitHeld = true;

    const buttons = Array.from(
      /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component')),
      (container) => container.refs.addToCartButton
    );
    const busyTimeout = setTimeout(() => {
      for (const button of buttons) button?.setAttribute('aria-disabled', 'true');
      this.#setButtonsBusy(true);
    }, HELD_SUBMIT_BUSY_DELAY);

    const isApplied = await picker.whenSettled();

    clearTimeout(busyTimeout);
    for (const button of buttons) button?.removeAttribute('aria-disabled');
    this.#setButtonsBusy(false);
    this.#isSubmitHeld = false;

    if (!this.isConnected) return;

    if (!isApplied) {
      const message = this.dataset.addToCartError || 'Add to cart failed';
      this.#showAddToCartError(message);
      // Lets the sticky bar, which clicked on the shopper's behalf, stop waiting for the add
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * Shows the spinner on the form's add to cart buttons while an add is loading.
   * @param {boolean} isBusy
   */
  #setButtonsBusy(isBusy) {
    for (const container of /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    )) {
      const button = container.refs.addToCartButton;
      if (isBusy) button?.setAttribute('aria-busy', 'true');
      else button?.removeAttribute('aria-busy');
    }
  }

  /**
   * Shows an add to cart error beside the buttons and announces it, then hides both.
   * @param {string} message
   */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    // Create or get existing error live region for screen readers
    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');

      // Clear the announcement
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Keeps accelerated checkout from buying the previous variant while a selection loads.
   * @param {EventTarget | null} source - The picker that dispatched the selection.
   */
  #holdAcceleratedCheckout(source) {
    const picker = /** @type {import('@theme/variant-picker').default | null} */ (
      source instanceof HTMLElement && 'whenSettled' in source ? source : null
    );
    const { acceleratedCheckoutButtonContainer } = this.refs;
    if (!picker?.isPending || !acceleratedCheckoutButtonContainer) return;

    acceleratedCheckoutButtonContainer.inert = true;
    picker.whenSettled().then(() => {
      if (this.refs.acceleratedCheckoutButtonContainer) this.refs.acceleratedCheckoutButtonContainer.inert = false;
    });
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#isAdding) return;
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    } else if (!formData.has('quantity')) {
      formData.set('quantity', '1');
    }

    // A saved personalization swaps in its personalized variant, which the add-ons then nest under.
    // `form.id` would return the form's `id` input, not its attribute.
    const personalization = /** @type {(HTMLElement & { applyTo?(formData: FormData): void }) | null} */ (
      document.querySelector(`product-personalization[data-form-id="${form.getAttribute('id')}"]`)
    );
    personalization?.applyTo?.(formData);

    const addOnVariantIds = overrideVariantId ? [] : this.#getCheckedAddOnVariantIds(form);
    if (addOnVariantIds.length === 0) {
      this.#addToCart(formData, event);
      return;
    }

    const variantId = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity')) || 1;
    const sellingPlanId = formData.get('selling_plan');

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData) {
      const propertyName = key.match(/^properties\[(.+)\]$/)?.[1];
      if (propertyName && typeof value === 'string') properties[propertyName] = value;
    }

    this.#addToCart(
      [
        {
          id: variantId,
          quantity,
          ...(sellingPlanId && { selling_plan: Number(sellingPlanId) }),
          ...(Object.keys(properties).length > 0 && { properties }),
        },
        ...addOnVariantIds.map((addOnVariantId) => ({
          id: Number(addOnVariantId),
          quantity,
          parent_id: variantId,
        })),
      ],
      event
    );
  }

  /**
   * Reads the add-ons checked for this form. They sit outside it and join it through their `form` attribute.
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  #getCheckedAddOnVariantIds(form) {
    return Array.from(form.elements)
      .filter((element) => element instanceof HTMLInputElement && element.matches('[data-add-on-input]:checked'))
      .map((element) => /** @type {HTMLInputElement} */ (element).value);
  }

  /**
   * Posts an add to the cart, then reports it through the cart event, the error message and the live region.
   * A single line goes as the form data; several lines, or lines nested under a parent, go as JSON items.
   * @param {FormData | CartAddItem[]} payload
   * @param {Event} [event] - The user action whose add is measured.
   */
  #addToCart(payload, event) {
    const { addToCartTextError } = this.refs;

    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }
    const sections = cartItemComponentsSectionIds.join(',');

    /** @type {Array<{merchandiseId: string, quantity: number}>} */
    let lines;
    /** @type {RequestInit} */
    let request;

    if (payload instanceof FormData) {
      const variantId = payload.get('id');
      if (!variantId) throw new Error('Form ID is required');

      if (sections) payload.append('sections', sections);
      lines = [
        {
          merchandiseId: /** @type {string} */ (variantId),
          quantity: Number(payload.get('quantity')) || 1,
        },
      ];

      const fetchCfg = fetchConfig('javascript', { body: payload });
      request = { ...fetchCfg, headers: { ...fetchCfg.headers, Accept: 'text/html' } };
    } else {
      if (payload.length === 0) return;

      lines = payload.map((item) => ({ merchandiseId: item.id.toString(), quantity: item.quantity }));
      request = fetchConfig('json', { body: JSON.stringify({ items: payload, sections }) });
    }

    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferredEventPromise.promise,
      })
    );

    this.#isAdding = true;
    this.#setButtonsBusy(true);

    fetch(Theme.routes.cart_add_url, request)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          this.#showAddToCartError(response.message);
          this.#showQuietAddToast(response.message || this.dataset.addToCartError);
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        // Add aria-live region to inform screen readers that the item was added
        // Get the added text from any add-to-cart button
        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (this.#isQuietAdd) {
          this.#showQuietAddToast();
        } else if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        // Fetch the updated cart to get the actual total quantity for this variant
        await this.#refreshCart()
          .then((ajaxCart) => {
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: ajaxCart.items,
                source: 'product-form-component',
                sourceId: this.id.toString(),
                itemCount,
                productId: this.dataset.productId,
                sections: response.sections,
                didError: false,
              },
            });

            this.#updateCartQuantity(ajaxCart);
          })
          .catch(deferredEventPromise.reject);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);
        this.#showQuietAddToast(this.dataset.addToCartError);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#isAdding = false;
        this.#setButtonsBusy(false);

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * A quiet add leaves the cart closed, so the card confirms it.
   */
  get #isQuietAdd() {
    return this.hasAttribute('data-quiet-add');
  }

  /**
   * Shows the card's toast for a quiet add and announces it.
   * @param {string} [errorMessage] - Shows the toast as an error with this message.
   */
  #showQuietAddToast(errorMessage) {
    const { quietAddToast, quietAddToastText } = this.refs;
    if (!this.#isQuietAdd || !quietAddToast || !quietAddToastText) return;

    clearTimeout(quietAddToastTimeout);
    quietAddToastOwner?.#hideQuietAddToast();

    const message = errorMessage ?? quietAddToastText.dataset.addedText ?? '';
    quietAddToastText.textContent = message;
    quietAddToast.classList.toggle('quick-add__toast--error', errorMessage !== undefined);
    quietAddToast.classList.add('quick-add__toast--visible');
    quietAddToastOwner = this;

    // A live region rewritten with the text it already holds is not announced again
    this.#quietAddAnnounceTimeout = setTimeout(() => this.#setLiveRegionText(message), QUIET_ADD_ANNOUNCE_DELAY);

    quietAddToastTimeout = setTimeout(() => this.#hideQuietAddToast(), QUIET_ADD_TOAST_DURATION);
  }

  /**
   * Hides the card's quiet add toast and empties its live region.
   */
  #hideQuietAddToast() {
    clearTimeout(this.#quietAddAnnounceTimeout);
    this.refs.quietAddToast?.classList.remove('quick-add__toast--visible');
    this.#clearLiveRegionText();
    if (quietAddToastOwner === this) quietAddToastOwner = null;
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    this.#holdAcceleratedCheckout(event.target);

    try {
      const { detail } = await event.promise;
      // A morphed `<main>` already carries the new form, and the morph moved it out of `html`
      if (!detail?.html || detail.morphElementSelector) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      const { quantitySelector } = this.refs;
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
      }

      // The input is not re-rendered, so it takes the fetched variant's sold-out state
      if (quantitySelector?.refs?.quantityInput && newQuantityInput) {
        quantitySelector.refs.quantityInput.disabled = newQuantityInput.disabled;
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
    }
  };

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
